import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coins, Dice5, Dices, Spade, Zap, Bomb, ArrowUpDown, Club, Flame, Hash, Disc3, Layers, Star, ChevronRight, Sparkles, Footprints } from "lucide-react";
import { StreakBadges } from "./StreakBadges";
import api, { imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmt } from "../lib/format";
import { toast } from "sonner";
import { TilePreview } from "./TilePreview";
import { useMissions, ProgressRing } from "./Missions";

export const MINI_GAMES = [
  { id: "money", to: "/mini/slot/money", name: "Money Rush", tag: "3 reels · bonus x10 · Lucky Wheel", icon: Coins, bg: "linear-gradient(135deg,#166534,#052e16)" },
  { id: "seven", to: "/mini/live/seven", name: "7 Up 7 Down LIVE", tag: "Live table · 2 dice · seven 5x · exact 30x", icon: Dices, bg: "linear-gradient(135deg,#1d4ed8,#1e1b4b)" },
  { id: "s777", to: "/mini/pick/s777", name: "Lucky 777", tag: "777♦ · number 5x · exact 20x", icon: Sparkles, bg: "linear-gradient(135deg,#7c2d12,#1c0a02)" },
  { id: "chicken", to: "/mini/round/chicken", name: "Chicken Road", tag: "cross lanes · cash out anytime", icon: Footprints, bg: "linear-gradient(135deg,#0f766e,#022c22)" },
  { id: "baccarat", to: "/mini/pick/baccarat", name: "Baccarat", tag: "player · banker · tie 9x", icon: Spade, bg: "linear-gradient(135deg,#7f1d1d,#1c0a0a)" },
  { id: "blackjack", to: "/mini/round/blackjack", name: "Blackjack 21", tag: "beat the dealer · 2.5x", icon: Layers, bg: "linear-gradient(135deg,#065f46,#022c22)" },
  { id: "dice", to: "/mini/dice", name: "Royal Dice", tag: "up to 4x", icon: Dice5, bg: "linear-gradient(135deg,#b91c1c,#7f1d1d)" },
  { id: "sicbo", to: "/mini/pick/sicbo", name: "Sic Bo", tag: "3 dice · totals up to 60x", icon: Dices, bg: "linear-gradient(135deg,#9a3412,#1c0a02)" },
  { id: "matka", to: "/mini/pick/matka", name: "Matka Instant", tag: "open · close · jodi 90x", icon: Hash, bg: "linear-gradient(135deg,#1e293b,#020617)" },
  { id: "poker", to: "/mini/pick/poker", name: "Poker Hi-Card", tag: "5-card showdown · 1.95x", icon: Club, bg: "linear-gradient(135deg,#312e81,#0b0a2a)" },
  { id: "teen_patti", to: "/mini/pick/teen_patti", name: "Teen Patti", tag: "1.95x", icon: Layers, bg: "linear-gradient(135deg,#991b1b,#450a0a)" },
  { id: "andar_bahar", to: "/mini/andar-bahar", name: "Andar Bahar", tag: "1.9x", icon: Spade, bg: "linear-gradient(135deg,#059669,#064e3b)" },
  { id: "dragon_tiger", to: "/mini/pick/dragon_tiger", name: "Dragon Tiger", tag: "tie 9x", icon: Flame, bg: "linear-gradient(135deg,#ea580c,#7c2d12)" },
  { id: "roulette", to: "/mini/pick/roulette", name: "Royal Roulette", tag: "number 36x", icon: Disc3, bg: "linear-gradient(135deg,#15803d,#14532d)" },
  { id: "lucky7", to: "/mini/pick/lucky7", name: "Lucky 7", tag: "up to 10x", icon: Club, bg: "linear-gradient(135deg,#0e7490,#164e63)" },
  { id: "hilo", to: "/mini/round/hilo", name: "Hi-Lo", tag: "streak multiplier", icon: ArrowUpDown, bg: "linear-gradient(135deg,#0f766e,#134e4a)" },
  { id: "mines", to: "/mini/round/mines", name: "Mines", tag: "cash out anytime", icon: Bomb, bg: "linear-gradient(135deg,#1d4ed8,#1e1b4b)" },
  { id: "lucky_number", to: "/mini/pick/lucky_number", name: "Lucky 99", tag: "exact 95x", icon: Hash, bg: "linear-gradient(135deg,#4f46e5,#312e81)" },
];

let imgCache = null, visCache = null;
const DEFAULT_VISIBLE = [];
export function useMiniImages() {
  const [imgs, setImgs] = useState(imgCache || {});
  const [vis, setVis] = useState(visCache || DEFAULT_VISIBLE);
  useEffect(() => { if (imgCache) return; api.get("/config").then((r) => { imgCache = r.data?.mini_game_images || {}; visCache = Array.isArray(r.data?.mini_visible_games) ? r.data.mini_visible_games : DEFAULT_VISIBLE; setImgs(imgCache); setVis(visCache); }).catch(() => {}); }, []);
  return imgs;
}
/* Games the admin has switched ON for customers (settings.mini_visible_games). */
export function useVisibleMinis() {
  // Unknown until /config answers → show NOTHING (never flash the default set when the admin has switched games off).
  const [vis, setVis] = useState(visCache || []);
  useEffect(() => { if (visCache) { setVis(visCache); return; } api.get("/config").then((r) => { visCache = Array.isArray(r.data?.mini_visible_games) ? r.data.mini_visible_games : DEFAULT_VISIBLE; setVis(visCache); }).catch(() => {}); }, []);
  return MINI_GAMES.filter((g) => vis.includes(g.id));
}

export function useMiniTiles() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const imgs = useMiniImages();
  const favs = user?.fav_minis || [];
  const open = (g) => { if (!user) { toast.info("Login to play"); navigate("/auth"); return; } navigate(g.to); };
  const toggleFav = async (e, id) => {
    e.stopPropagation();
    if (!user) { toast.info("Login to pin favourites"); return; }
    const next = favs.includes(id) ? favs.filter((f) => f !== id) : [...favs, id];
    setUser({ ...user, fav_minis: next });
    try { await api.put("/mini/favourites", { games: next }); toast.success(favs.includes(id) ? "Removed from favourites" : "Pinned to top"); } catch {}
  };
  const sortFav = (arr) => [...arr].sort((a, b) => (favs.includes(b.id) ? 1 : 0) - (favs.includes(a.id) ? 1 : 0));
  return { user, imgs, favs, open, toggleFav, sortFav };
}

export function MiniTile({ g, compact, imgs, favs, open, toggleFav, tid = "mini-tile" }) {
  const missions = useMissions();
  const mission = missions?.missions?.find((m) => m.game === g.id);
  return (
    <div role="button" tabIndex={0} onClick={() => open(g)} onKeyDown={(e) => e.key === "Enter" && open(g)} data-testid={`${tid}-${g.id}`}
      className={`rl-casino-tile relative rounded-xl text-white text-left active:scale-95 transition-transform overflow-hidden cursor-pointer ${compact ? "h-24" : "h-32"}`} style={{ background: g.bg }}>
      {imgs[g.id] ? <img src={imgUrl(imgs[g.id])} alt={g.name} className="absolute inset-0 w-full h-full object-cover rl-casino-tile-img" /> : <g.icon className={`absolute -right-2 -bottom-2 opacity-20 ${compact ? "w-12 h-12" : "w-16 h-16"}`} />}
      <span className="rl-casino-tile-shine" aria-hidden="true" />
      <TilePreview id={g.id} />
      <button onClick={(e) => toggleFav(e, g.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/40 flex items-center justify-center" data-testid={`${tid === "mini-tile" ? "mini-fav" : tid + "-fav"}-${g.id}`} aria-label="favourite">
        <Star className={`w-3.5 h-3.5 ${favs.includes(g.id) ? "text-amber-400 fill-amber-400" : "text-white/70"}`} />
      </button>
      {mission && <span className={`absolute bottom-1.5 right-1.5 z-10 rounded-full ${mission.done ? "bg-emerald-600/90" : "bg-black/60"} text-white`} data-testid={`tile-mission-${g.id}`}><ProgressRing value={mission.progress} max={mission.target} done={mission.done} size={compact ? 26 : 30} stroke={3}>{mission.done ? "✓" : `${mission.progress}/${mission.target}`}</ProgressRing></span>}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-1.5 pt-5">
        <div className={`rl-casino-tile-title leading-tight ${compact ? "text-[9px]" : "text-xs"}`}>{g.name}</div>
        <div className="text-[8px] text-amber-300/90 font-semibold tracking-wide uppercase">{g.tag}</div>
      </div>
    </div>
  );
}


export function MiniGamesGrid({ compact = false }) {
  const tiles = useMiniTiles();
  const visible = useVisibleMinis();
  const [all, setAll] = useState(!compact);
  const { user } = tiles;
  const ordered = tiles.sortFav(visible);
  const list = all ? ordered : ordered.slice(0, 8);
  if (!visible.length) return null;
  return (
    <div data-testid="mini-games-grid" className="rl-casino-panel rounded-2xl p-3">
      <div className="flex items-center justify-between mb-2">
        <h2 className="rl-casino-heading flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" />Instant Games <span className="text-[10px] font-bold text-amber-200/60">{visible.length}</span></h2>
        {compact && visible.length > 8 && <button onClick={() => setAll((a) => !a)} className="text-xs font-bold text-amber-300" data-testid="mini-toggle-all">{all ? "Show less" : "See all ›"}</button>}
      </div>
      <StreakBadges user={user} />
      <div className={`grid gap-2 ${compact ? "grid-cols-4" : "grid-cols-3"}`}>
        {list.map((g) => <MiniTile key={g.id} g={g} compact={compact} {...tiles} />)}
      </div>
    </div>
  );
}

/* Home top strip: 4 smart picks (favourites first, then the house table games) in one swipeable row */
export function MiniTopStrip() {
  const tiles = useMiniTiles();
  const visible = useVisibleMinis();
  const navigate = useNavigate();
  const list = tiles.sortFav(visible).slice(0, 5);
  if (!visible.length) return null;
  return (
    <div className="rl-casino-panel rounded-2xl p-2.5" data-testid="mini-top-strip">
      <div className="flex items-center justify-between mb-1.5 px-0.5">
        <span className="rl-casino-heading text-[11px] flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-400" />Casino Tables</span>
        <button onClick={() => navigate("/play")} className="text-[10px] font-bold text-amber-300 flex items-center" data-testid="mini-top-all">All {visible.length} <ChevronRight className="w-3 h-3" /></button>
      </div>
      <div className="flex gap-2 overflow-x-auto snap-x pb-0.5" style={{ scrollbarWidth: "none" }}>
        {list.map((g) => <div key={g.id} className="snap-start shrink-0 w-[31%] min-w-[104px]"><MiniTile g={g} compact tid="top-tile" {...tiles} /></div>)}
      </div>
    </div>
  );
}

export function BigWinTicker() {
  const [wins, setWins] = useState([]);
  useEffect(() => {
    const load = () => api.get("/mini/wins/recent").then((r) => setWins(r.data)).catch(() => {});
    load(); const t = setInterval(load, 20000); return () => clearInterval(t);
  }, []);
  if (!wins.length) return null;
  const name = (id) => MINI_GAMES.find((g) => g.id === id)?.name || id;
  const items = [...wins, ...wins];
  return (
    <div className="rounded-xl overflow-hidden text-white text-xs font-semibold py-1.5" style={{ background: "linear-gradient(90deg,#7f1d1d,#0f172a)" }} data-testid="big-win-ticker">
      <div className="flex whitespace-nowrap rl-marquee" style={{ animationDuration: `${Math.max(18, wins.length * 4)}s` }}>
        {items.map((w, i) => <span key={i} className="px-4">★ <b className="text-amber-300">{w.name}</b> won <b className="text-emerald-300">{fmt(w.payout)}</b> on {name(w.game)}</span>)}
      </div>
    </div>
  );
}

export function MiniLeaderboard() {
  const [rows, setRows] = useState([]);
  const [streaks, setStreaks] = useState([]);
  useEffect(() => {
    api.get("/mini/leaderboard").then((r) => setRows(r.data)).catch(() => {});
    api.get("/mini/leaderboard/streaks").then((r) => setStreaks(r.data)).catch(() => {});
  }, []);
  if (!rows.length && !streaks.length) return null;
  const medal = ["🥇", "🥈", "🥉"];
  return (
    <div className="rl-card p-3" data-testid="mini-leaderboard">
      {streaks.length > 0 && (
        <div className="mb-3" data-testid="streak-leaderboard">
          <h2 className="font-display font-bold text-slate-900 text-sm mb-1.5">🔥 Hottest Win Streaks</h2>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {streaks.map((r, i) => (
              <div key={i} className="shrink-0 rounded-xl px-3 py-1.5 text-white text-xs flex items-center gap-2" style={{ background: "linear-gradient(120deg,#ea580c,#f59e0b)" }} data-testid={`streak-row-${i}`}>
                <span className="font-black text-lg leading-none">{r.streak}</span><span className="leading-tight"><b>{r.name}</b><br /><span className="text-[9px] opacity-90">wins in a row · best {r.best}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
      {rows.length > 0 && <h2 className="font-display font-bold text-slate-900 text-sm mb-2">🏆 Today's Top Winners</h2>}
      <div className="divide-y divide-slate-100">
        {rows.slice(0, 5).map((r, i) => (
          <div key={i} className="flex items-center justify-between py-1.5 text-xs" data-testid={`leader-row-${i}`}>
            <span className="flex items-center gap-2"><span className="w-5 text-center">{medal[i] || i + 1}</span><b className="text-slate-800">{r.name}</b><span className="text-slate-400">{r.wins} wins · {r.games.slice(0, 2).map((g) => g.replace("_", " ")).join(", ")}</span></span>
            <span className="font-mono font-black text-emerald-600">{fmt(r.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
